import { useCallback, useRef, useState } from 'react';

const DEFAULT_ENTER_DELAY = 1;
const DEFAULT_EXIT_DELAY = 0;
const MIN_EXIT_DELAY = 0;
const MIN_ENTER_DELAY = 0;

interface Options {
  enterDelay?: number;
  exitDelay?: number;
  onUnmount?: () => void;
}

interface Result {
  mounted: boolean;
  rendered: boolean;
}

const useDelayedRender = (active = false, options: Options = {}): Result => {
  const [, force] = useState({});
  const mounted = useRef(active);
  const rendered = useRef(false);
  const renderTimer = useRef<NodeJS.Timeout | null>(null);
  const unmountTimer = useRef<NodeJS.Timeout | null>(null);
  const prevActive = useRef(active);

  const recalculate = useCallback(() => {
    const { enterDelay = DEFAULT_ENTER_DELAY, exitDelay = DEFAULT_EXIT_DELAY } =
      options;

    if (prevActive.current) {
      // Mount immediately
      mounted.current = true;
      if (unmountTimer.current) clearTimeout(unmountTimer.current);

      if (enterDelay <= MIN_ENTER_DELAY) {
        // Render immediately
        rendered.current = true;
      } else {
        if (renderTimer.current) return;

        // Render after a delay
        renderTimer.current = setTimeout(() => {
          rendered.current = true;
          renderTimer.current = null;
          force({});
        }, enterDelay);
      }
    } else {
      // Immediately set to unrendered
      rendered.current = false;

      if (exitDelay <= MIN_EXIT_DELAY) {
        mounted.current = false;
      } else {
        if (unmountTimer.current) return;

        // Unmount after a delay
        unmountTimer.current = setTimeout(() => {
          mounted.current = false;
          unmountTimer.current = null;
          force({});
        }, exitDelay);
      }
    }
  }, [options]);

  // When the active prop changes, need to re-calculate
  if (active !== prevActive.current) {
    prevActive.current = active;
    // We want to do this synchronously with the render, not in an effect
    // this way when active → true, mounted → true in the same pass
    recalculate();
  }

  return {
    mounted: mounted.current,
    rendered: rendered.current,
  };
};

export default useDelayedRender;
